import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DSSetup, Service, ValidationErrors } from 'webapp-types';
import { MetricFilterOperator } from 'webapp/enums/enums';
import { copyObject } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';
import FormSelectComponent from 'webapp/components/form/select';
import type { Options } from 'webapp/components/form/select-attributes';
import FormSelectAttributesComponent from 'webapp/components/form/select-attributes';
import FormSelectDimensionsComponent from 'webapp/components/form/select-dimensions';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { FilterItem, MetricSetup } from 'webapp/models-custom';
import MetricSetupForMasterFilter from 'webapp/models-custom/metric-setup-for-master-filter';
import { MAX_ATTRIBUTE_LENGTH } from 'webapp/models-custom/visualization/base';

import type { RSjqXHR, RSPromise } from 'webapp/services/request';

interface Args {
	model: FilterItem;
	isEdit?: boolean;
	editedItem?: FilterItem | null; // the original of @model in @metricSetup, when editing
	metricSetup: MetricSetup | MetricSetupForMasterFilter;
	onSave: () => void;
	addMore?: () => void; // only in master filter popup
	onContentChange?: () => void;
	theme?: string;
}

export default class BlockFiltersEditItemComponent extends Component<Args> {
	@service('constants') declare constantsService: Service.Constants;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/horizon-metrics')
	declare mmHorizonMetricsService: Service.ModelManager.HorizonMetrics;

	// used for dimension selector and querying dimension values
	metricSetup: MetricSetup | MetricSetupForMasterFilter;

	@tracked operatorsLimitationsPromiseXHR: RSjqXHR | null = null;

	@tracked allowedOperators = [MetricFilterOperator.ANY_OF];

	@tracked errors: ValidationErrors<'field' | 'operator' | 'values'> = {};

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.metricSetup = this.args.metricSetup.createCopy();

		this.onModelChange();
	}

	get model() {
		return this.args.model;
	}

	get isLoadingOperators() {
		return !!this.operatorsLimitationsPromiseXHR;
	}

	@cached
	get operatorOptions() {
		// the dialog's setup keeps the block's breakdown; this.metricSetup has the edited field as its dimension
		const metricSetup = this.args.metricSetup;
		const isMasterFilter = metricSetup instanceof MetricSetupForMasterFilter;
		// neutral on purpose: the limitations don't say why (unsupported, or Horizon unreachable)
		const notAvailableText = isMasterFilter
			? 'Not available for any metric on this board'
			: 'Not available for this metric';
		// master filter: a union across the board's metrics; blocks that can't take it flag "filter not applied"
		const allowedOperators = isMasterFilter
			? [
					...[MetricFilterOperator.ANY_OF, MetricFilterOperator.NONE_OF].filter(
						(op) => !this.allowedOperators.includes(op),
					),
					...this.allowedOperators,
				]
			: metricSetup.allowedOperatorsForField(
					this.model.field,
					this.allowedOperators,
					this.args.editedItem ?? undefined,
				);
		return this.constantsService.metricFilterOperators.map((item) => {
			const disabled = !allowedOperators.includes(item.value as MetricFilterOperator);
			return {
				value: item.value,
				label: item.label,
				disabled,
				description: disabled ? notAvailableText : undefined,
			};
		});
	}

	get noOperatorAllowedText() {
		if (this.isLoadingOperators || this.operatorOptions.some((item) => !item.disabled)) {
			return null;
		}
		const fieldLabel = this.metricSetup.getDimensionNameForDimension(this.model.field);
		return `No filters are available for ${fieldLabel} on this metric.`;
	}

	get maxAttributeLength() {
		// allow multiple for all selections, will show warning in sidebar if "too many"
		return MAX_ATTRIBUTE_LENGTH;
	}

	get firstValue() {
		return this.model.values[0] || '';
	}

	set firstValue(value: string) {
		this.changeValues(value ? [value] : []);
	}

	get isValuesAttributeNames() {
		return this.isOperatorAttributeNames(this.model.operator);
	}

	get isSubmitDisabled() {
		return !this.firstValue;
	}

	get submitIcon(): IconName | undefined {
		return this.args.isEdit ? undefined : 'Plus';
	}

	get submitText() {
		return this.args.isEdit ? 'Update filter' : 'Add filter';
	}

	get isCollapsedMasterFilterMode() {
		return !!this.args.addMore;
	}

	get horizonMetricObjectId() {
		// resolve needs a signed-in user: master filter has no single metric, public views have no metric
		if (this.metricSetup instanceof MetricSetupForMasterFilter) {
			return null;
		}
		const metric = this.metricSetup.metric;
		return metric?.isTypeHorizon ? metric.objectId : null;
	}

	get loadValueOptions() {
		return this.horizonMetricObjectId ? this.loadHorizonValueOptions : undefined;
	}

	@action
	changeField(fields: string[]) {
		const field = fields[0] || '';
		this.model.field = field;
		this.metricSetup.dimensions = [field];
		this.removeValidationErrorOnProp('field');
		this.changeValues([]);

		void this.updateOperatorOptions().then(() => {
			const operator = this.metricSetup.getFirstAvailableMetricOperator();
			this.changeOperator(operator);
		});
	}

	@action
	changeOperator(value: string | null) {
		const operator = value as MetricFilterOperator;
		const resetValues = this.isOperatorAttributeNames(operator) !== this.isValuesAttributeNames;
		this.model.operator = operator;
		this.removeValidationErrorOnProp('operator');
		if (resetValues) {
			this.changeValues([]);
		}
	}

	@action
	changeValues(values: string[]) {
		this.model.values = values;
		this.removeValidationErrorOnProp('values');
	}

	@action
	async loadHorizonValueOptions(): Promise<Options> {
		const field = this.model.field;
		const options = await this.mmHorizonMetricsService.getValueOptionsForField(
			this.metricSetup as MetricSetup,
			field,
			this.model.operator,
			undefined,
			// the block's splits; this.metricSetup holds only the edited field
			this.args.metricSetup.dimensions,
		);
		// field changed while loading
		return this.model.field === field ? options : [];
	}

	@action
	saveItem() {
		if (!this.validate()) {
			return;
		}

		this.args.onSave();
	}

	@action
	onModelChange() {
		this.errors = {};
		this.metricSetup.dimensions = this.args.model.field ? [this.args.model.field] : [];
		if (this.args.model.field) {
			void this.updateOperatorOptions();
		}
	}

	@action
	onContentChange() {
		this.args.onContentChange?.();
	}

	@action
	onOpen(cb: () => void) {
		if (this.isLoadingOperators) {
			return false;
		}

		const alreadyLoadedOperators = this.mmMetricsService.getCachedOperatorLimitations(
			this.getMetricSetupDataArray(),
		);
		if (alreadyLoadedOperators) {
			return false;
		}

		void this.updateOperatorOptions().then(() => {
			scheduleOnce('afterRender', this, cb);
		});

		return true; // requery every time we open a dropdown
	}

	private getMetricSetupDataArray(): DSSetup.MetricSetupWithoutFiltersData[] {
		const dimension = this.model.field;

		const metricDataArray =
			this.metricSetup instanceof MetricSetupForMasterFilter
				? this.metricSetup._metricData
				: [this.metricSetup.metricData!];
		return metricDataArray.map((md) => {
			return Object.assign(
				{
					dimensions: [dimension],
				},
				copyObject(md),
			);
		});
	}

	private updateOperatorOptions() {
		if (this.operatorsLimitationsPromiseXHR) {
			this.operatorsLimitationsPromiseXHR.abort();
			this.operatorsLimitationsPromiseXHR = null;
		}

		if (!this.model.field) {
			return Promise.resolve();
		}

		const metricSetupDataArray = this.getMetricSetupDataArray();

		const alreadyLoadedOperators =
			this.mmMetricsService.getCachedOperatorLimitations(metricSetupDataArray);

		if (alreadyLoadedOperators) {
			this.allowedOperators = alreadyLoadedOperators;
			return Promise.resolve();
		} else {
			let promise: RSPromise<unknown> =
				this.mmMetricsService.getMetricOperatorLimitationsForDimension(
					metricSetupDataArray,
					this.model.field,
				);

			const jqPromise = promise.jqPromise as RSjqXHR;
			this.operatorsLimitationsPromiseXHR = jqPromise;

			promise = promise.then((allowedOperators) => {
				if (jqPromise === this.operatorsLimitationsPromiseXHR) {
					this.allowedOperators = allowedOperators as MetricFilterOperator[];
					this.operatorsLimitationsPromiseXHR = null;
				}
			});

			return promise;
		}
	}

	private isOperatorAttributeNames(operator: MetricFilterOperator) {
		return (
			!!operator &&
			[MetricFilterOperator.ANY_OF, MetricFilterOperator.NONE_OF].includes(operator)
		);
	}

	private validate() {
		const errors: ValidationErrors<'field' | 'operator' | 'values'> = {};

		if (!this.model.field) {
			errors.field = [{ message: 'Dimension is required' }];
		}

		if (!this.model.operator) {
			errors.operator = [{ message: 'Operator is required' }];
		}

		if (!this.model.values.length) {
			errors.values = [
				{
					message: this.isValuesAttributeNames
						? 'Dimension values are required'
						: 'Value is required',
				},
			];
		}
		this.errors = errors;

		return Object.keys(errors).length === 0;
	}

	private removeValidationErrorOnProp(prop: 'field' | 'operator' | 'values') {
		delete this.errors[prop];
		this.errors = { ...this.errors };
	}

	<template>
		<div class="row ms-filters-form" {{didUpdate this.onModelChange @model}}>
			<label>Filter</label>
			{{#if @addMore}}
				<a class="action-add" {{on "click" @addMore}}>
					Add more filters
					<CommonUiIconComponent @name="Plus" @size="sm" />
				</a>
			{{/if}}
			<div class="row">
				<FormSelectDimensionsComponent
					@metricSetup={{this.metricSetup}}
					@onChange={{this.changeField}}
					@showFloatlabel={{false}}
					@theme={{@theme}}
					@skipNone={{true}}
					@allowEmpty={{true}}
				/>
				<RowErrorComponent @items={{this.errors.field}} />
			</div>
			{{#if this.model.field}}
				<div class="row" {{didInsert this.onContentChange}}>
					<FormSelectComponent
						name="operator"
						@selected={{this.model.operator}}
						@options={{this.operatorOptions}}
						@onChange={{this.changeOperator}}
						@showFloatlabel={{false}}
						@isRequired={{true}}
						@isNumeric={{false}}
						@isLoading={{this.isLoadingOperators}}
						@tooltipOnSelectionFromProperty=""
						@tooltipOnListFromProperty="description"
						@onOpen={{this.onOpen}}
						@extraClass="dropdown-operator"
						@theme={{@theme}}
					/>
					{{#if this.noOperatorAllowedText}}
						<small class="ms-filters-note">{{this.noOperatorAllowedText}}</small>
					{{/if}}
					<RowErrorComponent @items={{this.errors.operator}} />
				</div>
				<div class="row">
					{{#if this.isValuesAttributeNames}}
						<FormSelectAttributesComponent
							@selected={{this.model.values}}
							@metricSetupForQuery={{this.metricSetup}}
							@onChange={{this.changeValues}}
							@isMultiple={{true}}
							@showFloatlabel={{false}}
							@placeholder="Select value"
							@maxSelectionLength={{this.maxAttributeLength}}
							@loadOptions={{this.loadValueOptions}}
							@theme={{@theme}}
						/>
					{{else}}
						<FormInputComponent
							name="firstValue"
							maxlength="50"
							placeholder="Text"
							@value={{this.firstValue}}
						/>
					{{/if}}
					<RowErrorComponent @items={{this.errors.values}} />
				</div>
			{{/if}}
			{{#unless this.isCollapsedMasterFilterMode}}
				<SubmitButtonComponent
					@theme="blue-outlined"
					@icon={{this.submitIcon}}
					@text={{this.submitText}}
					@extraClass={{if this.isSubmitDisabled "disabled"}}
					{{! we still want to trigger action even if "greyed out"}}
					@onTrigger={{this.saveItem}}
				/>
			{{/unless}}
		</div>
	</template>
}
